import { IAuctionOffer } from 'shared/types/AuctionOffer';
import { IPriceLocalized } from 'shared/types/Price';
import { ISize } from 'shared/types/Size';
import { ISizesMap, IVariant } from 'productTemplate/types/SizesMap';
import { ITopOfferData } from 'productTemplate/types/TopOfferData';
import { IWebOffer } from 'shared/types/WebOffer';
import { PRICE_PRIORITY } from 'productTemplate/enums/pricePriority';

interface IOffersData {
  [key: string]: {
    topOffer: IPriceLocalized;
    lastSold: IPriceLocalized;
    sizeOption: ISize;
  };
}

// iterate through sizeOptions and grab the topOffer for the size if it exists
// and lastSoldPriceCents from sizesMap
export const getOffersData = ({
  buyBarSizesMap,
  existingOffers,
  sizeOptions,
  topOffers,
}: {
  buyBarSizesMap: ISizesMap;
  existingOffers?: IWebOffer[] | IAuctionOffer[];
  sizeOptions: ISize[];
  topOffers: ITopOfferData[];
}): IOffersData => {
  const result = {};

  if (!sizeOptions) {
    return result;
  }

  const topOffersMap = new Map(
    topOffers?.map(({ size, offerAmountCents }) => [size, offerAmountCents]),
  );
  const userOffersMap = new Map(
    (existingOffers as IWebOffer[])?.map((existingOffer) => [existingOffer.size, existingOffer]),
  );

  for (const sizeOption of sizeOptions) {
    const { value: size } = sizeOption;
    // add size to the result object
    result[size] = { sizeOption };

    // grab the top offer for the current size if it exists
    if (topOffersMap.has(size)) {
      result[size].topOffer = topOffersMap.get(size);
    }

    // grab the user's offer for the current size if it exists
    if (userOffersMap.has(size)) {
      result[size].userOffer = userOffersMap.get(size);
    }

    if (buyBarSizesMap?.[size]) {
      // iterate through conditions to find the first lastSoldPriceCents
      for (const condition of PRICE_PRIORITY) {
        const lastSoldPrice = buyBarSizesMap[size]?.[condition]?.lastSoldPriceCents;
        // If the size does not alrdy have a lastSold and the amount is greater than 0, set it as the lastSold
        if (!result[size].lastSold && lastSoldPrice?.amount > 0) {
          result[size].lastSold = { ...buyBarSizesMap[size][condition].lastSoldPriceCents };

          // Stop iterating after finding the first occurence
          break;
        }
      }
    }
  }

  return result;
};

const processOptions = ({ options, currentResult }) => {
  const { topOffer } = options;

  let result = currentResult;

  // if result does not have a topOffer, compare the current topOffer to 0
  if (topOffer?.amount > (result?.topOffer?.amount || 0)) {
    result = options;
  }

  return result;
};

export const getMakeOfferButtonVariant = ({
  sizes,
  offersMap,
}: {
  sizes: {
    sizeSearchFilter: string[] | null;
    userSize: string | null;
  };
  offersMap: {
    [key: string]: { topOffer?: IPriceLocalized; lastSold?: IPriceLocalized };
  };
}): IVariant => {
  const { sizeSearchFilter, userSize } = sizes;
  let filteredBySizeSearchFilters = null;
  let filteredByUserSize = null;
  let nonFilteredBySizeResult = null;

  for (const [size, options] of Object.entries(offersMap)) {
    // We prioritize by sizeSearchFilters => userSize => no size restrictions
    const isSizeSearchFilterMatch = sizeSearchFilter && sizeSearchFilter.includes(size);
    const isUserSizeMatch = userSize && userSize.toString() === size;

    if (isSizeSearchFilterMatch) {
      filteredBySizeSearchFilters = processOptions({
        currentResult: filteredBySizeSearchFilters,
        options,
      });
    } else if (isUserSizeMatch) {
      filteredByUserSize = processOptions({
        currentResult: filteredByUserSize,
        options,
      });
    } else {
      nonFilteredBySizeResult = processOptions({
        currentResult: nonFilteredBySizeResult,
        options,
      });
    }
  }

  return filteredBySizeSearchFilters || filteredByUserSize || nonFilteredBySizeResult;
};
